import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, Teleport as _Teleport } from "vue";
const _hoisted_1 = { class: "fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4" };
const _hoisted_2 = { class: "px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0" };
const _hoisted_3 = { class: "flex items-start gap-3 min-w-0" };
const _hoisted_4 = ["src", "alt"];
const _hoisted_5 = { class: "min-w-0" };
const _hoisted_6 = { class: "text-xl font-bold text-gray-900 truncate" };
const _hoisted_7 = {
    key: 0,
    class: "text-sm text-gray-500 mt-0.5 line-clamp-2"
};
const _hoisted_8 = { class: "flex items-start gap-3 flex-shrink-0" };
const _hoisted_9 = {
    key: 0,
    class: "text-xl font-bold text-gray-900 whitespace-nowrap"
};
const _hoisted_10 = { class: "flex-1 overflow-y-auto p-6" };
import { ref, computed, watch, onMounted, onUnmounted, provide, reactive, nextTick } from 'vue';
import { useTheme } from '@shared/composables/useTheme';
import ConfigContent from './ConfigContent.vue';
import ConfigFooter from './ConfigFooter.vue';
import eventBus from '@shared/utils/eventBus';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConfigModalBase',
    props: {
        isOpen: { type: Boolean },
        product: {},
        ingredientCategories: {},
        allIngredients: {},
        validationRef: {},
        priceMode: {}
    },
    emits: ["close", "add-to-cart", "pricing:configure", "pricing:reset"],
    setup(__props, { emit: __emit }) {
        _useCssVars(_ctx => ({
            "77eb7941": (themePrimaryColor.value),
            "73640c69": (themePrimaryHoverColor.value),
            "0c4cd29e": (themeBorderRadius.value)
        }));
        const { theme } = useTheme();
        // Unwrap theme values for v-bind() in styles with fallbacks
        const themePrimaryColor = computed(() => theme.value?.colors?.primary || '#2563eb');
        const themePrimaryHoverColor = computed(() => theme.value?.colors?.primaryHover || '#1d4ed8');
        const themeBorderRadius = computed(() => theme.value?.borderRadius || '0.5rem');
        const props = __props;
        const emit = __emit;
        // Preis-Label für den Header (Startpreis): „ab X" bei Größen, sonst Basispreis.
        const headerCurrencyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
        const headerPriceLabel = computed(() => {
            const p = props.product;
            if (!p)
                return '';
            if (p.hasSizes && p.priceRange && p.priceRange.min != null) {
                return `ab ${headerCurrencyFormatter.format(p.priceRange.min)}`;
            }
            const base = p.price != null ? Number(p.price) : (p.priceCent != null ? Number(p.priceCent) / 100 : null);
            return base != null && !Number.isNaN(base) ? headerCurrencyFormatter.format(base) : '';
        });
        // Modal state
        const selectedExtras = ref([]);
        const removedIngredients = ref([]);
        const specialInstructions = ref('');
        const quantity = ref(1);
        const selectedSize = ref(null);
        // ProductConfigContent ref for validation
        const productConfigContentRef = ref(null);
        const pricingState = reactive({
            price: 0,
            priceCent: 0,
            formattedPrice: '0,00 €',
            isLoading: false,
            breakdown: undefined,
            response: undefined,
            error: undefined
        });
        const pricingStateVersion = ref(0);
        const price = computed(() => pricingState.price ?? 0);
        const priceCent = computed(() => pricingState.priceCent ?? 0);
        const formattedPrice = computed(() => pricingState.formattedPrice ?? '0,00 €');
        const isLoading = computed(() => pricingState.isLoading ?? false);
        const pricingError = computed(() => pricingState.error ?? null);
        const lastPricingPayload = ref(null);
        const hasPricingConfig = computed(() => lastPricingPayload.value !== null);
        const createChannelId = () => {
            const cryptoRef = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
            if (cryptoRef?.randomUUID) {
                return `pricing-${cryptoRef.randomUUID()}`;
            }
            return `pricing-${Math.random().toString(36).slice(2)}`;
        };
        const pricingChannelId = ref(createChannelId());
        const resolveProductId = (product) => {
            if (!product) {
                return null;
            }
            return product.id ?? product._id ?? null;
        };
        // ✅ Stable key that only changes when product ID changes (not on size/price changes)
        const productComponentKey = computed(() => {
            const productId = resolveProductId(props.product);
            return productId ? `product-${productId}` : 'no-product';
        });
        const resetPricingState = () => {
            pricingState.price = 0;
            pricingState.priceCent = 0;
            pricingState.formattedPrice = '0,00 €';
            pricingState.isLoading = false;
            pricingState.breakdown = undefined;
            pricingState.response = undefined;
            pricingState.error = undefined;
            pricingStateVersion.value += 1;
        };
        const handlePricingResultEvent = (detail) => {
            if (!detail || detail.channelId !== pricingChannelId.value) {
                if (detail) {
                }
                return;
            }
            // 🔥 CRITICAL: Update pricingState directly - it's reactive!
            pricingState.price = detail.result.price ?? 0;
            pricingState.priceCent = detail.result.priceCent ?? 0;
            pricingState.formattedPrice = detail.result.formattedPrice ?? '0,00 €';
            pricingState.breakdown = detail.result.breakdown;
            pricingState.response = detail.result.response;
            pricingState.isLoading = detail.result.isLoading ?? false;
            pricingState.error = detail.result.error;
            // 🔥 CRITICAL: Use nextTick to ensure reactivity has settled
            pricingStateVersion.value += 1;
            // Force check: What do computed props see NOW?
            // Force check on serverPriceProp (the one passed to child)
        };
        const handlePricingLoadingEvent = (detail) => {
            if (!detail || detail.channelId !== pricingChannelId.value) {
                if (detail) {
                }
                return;
            }
            pricingState.isLoading = detail.isLoading;
            pricingStateVersion.value += 1;
        };
        function buildPricingPayload() {
            if (!props.product) {
                return null;
            }
            // ✅ Get mandatory ingredients selections from child component
            const childRef = productConfigContentRef.value;
            // ⚠️ CRITICAL: selectedMandatoryIngredients is already unwrapped by defineExpose!
            const mandatorySelections = childRef?.selectedMandatoryIngredients || {};
            console.log('🔍 [ConfigModalBase] Building pricing payload:', {
                hasMandatoryGroups: props.product?.mandatoryIngredients?.length || 0,
                mandatorySelections,
                selectionsType: typeof mandatorySelections,
                selectionsKeys: Object.keys(mandatorySelections)
            });
            // ✅ Convert Record<groupName, ingredientId> to Array format for server
            const selectedMandatoryIngredients = Object.entries(mandatorySelections).map(([groupName, ingredientId]) => {
                // Find the group to get its _id
                const group = props.product?.mandatoryIngredients?.find(g => g.name === groupName);
                // Find the ingredient to get its name
                const ingredient = group?.ingredients?.find((ing) => {
                    const ingId = typeof ing === 'object' ? (ing._id || ing.id) : ing;
                    return ingId === ingredientId;
                });
                const ingredientName = typeof ingredient === 'object' ? ingredient.name : (ingredient || 'Unbekannt');
                console.log('🔍 [ConfigModalBase] Converting mandatory selection:', {
                    groupName,
                    ingredientId,
                    ingredientName,
                    foundGroup: !!group,
                    groupId: group?._id
                });
                return {
                    groupId: group?._id?.toString() || groupName, // Use group._id if available, fallback to name
                    groupName: groupName,
                    selectedIngredients: [{
                            ingredientId: String(ingredientId),
                            name: ingredientName // ✅ Include ingredient name for cart display
                        }]
                };
            });
            const payload = {
                channelId: pricingChannelId.value,
                productId: props.product.id || props.product._id,
                quantity: quantity.value,
                selectedSizeId: selectedSize.value?.id ?? null,
                extraIngredients: selectedExtras.value.map((extra) => ({
                    id: extra.id,
                    name: extra.name,
                    price: extra.price,
                    priceCent: extra.priceCent || extra.price * 100
                })),
                removedIngredients: removedIngredients.value.map(ing => ing.id),
                selectedMandatoryIngredients: selectedMandatoryIngredients // ✅ Array format for server
            };
            return payload;
        }
        function emitPricingConfigure(_context) {
            const payload = buildPricingPayload();
            if (!payload) {
                return;
            }
            const payloadSignature = JSON.stringify(payload);
            const lastSignature = lastPricingPayload.value ? JSON.stringify(lastPricingPayload.value) : null;
            if (payloadSignature === lastSignature) {
                return;
            }
            lastPricingPayload.value = payload;
            pricingState.isLoading = true;
            emit('pricing:configure', payload);
        }
        function calculateInitialPrice() {
            if (!props.product) {
                return;
            }
            if (!selectedSize.value &&
                props.product?.hasSizes &&
                props.product?.sizes &&
                props.product.sizes.length > 0) {
                initializeDefaultSize({ triggerPricingUpdate: false });
            }
            emitPricingConfigure('initial');
        }
        function updatePricingConfiguration() {
            if (!props.product || !props.isOpen) {
                return;
            }
            emitPricingConfigure('update');
        }
        function initializeDefaultSize(options) {
            const triggerPricingUpdate = options?.triggerPricingUpdate ?? false;
            if (!props.product ||
                !props.product.hasSizes ||
                !props.product.sizes ||
                props.product.sizes.length === 0) {
                return null;
            }
            if (selectedSize.value) {
                return selectedSize.value;
            }
            const availableSizes = props.product.sizes;
            let resolvedDefaultSize = null;
            const defaultSizeCandidate = props.product.defaultSize;
            if (defaultSizeCandidate) {
                const matchingSize = availableSizes.find(size => size.id === defaultSizeCandidate.id);
                resolvedDefaultSize = matchingSize || defaultSizeCandidate;
            }
            if (!resolvedDefaultSize) {
                resolvedDefaultSize = availableSizes.reduce((cheapest, size) => {
                    const currentModifier = size.priceModifierCent ?? 0;
                    const cheapestModifier = cheapest.priceModifierCent ?? 0;
                    return currentModifier < cheapestModifier ? size : cheapest;
                }, availableSizes[0]);
            }
            if (!resolvedDefaultSize) {
                console.warn('⚠️ [ProductConfigModal] Could not resolve a default size despite available sizes');
                return null;
            }
            selectedSize.value = resolvedDefaultSize;
            if (triggerPricingUpdate && props.isOpen) {
                updatePricingConfiguration();
            }
            return selectedSize.value;
        }
        // Register event bus listeners immediately so we do not miss early events emitted during setup
        eventBus.on('productPricing:result', handlePricingResultEvent);
        eventBus.on('productPricing:loading', handlePricingLoadingEvent);
        // Lifecycle hooks
        onMounted(() => {
            if (props.isOpen && props.product) {
                initializeDefaultSize({ triggerPricingUpdate: false });
                calculateInitialPrice();
            }
        });
        watch(() => props.product, (newProduct, oldProduct) => {
            const newId = resolveProductId(newProduct);
            const oldId = resolveProductId(oldProduct);
            if (!newProduct || !newId) {
                return;
            }
            const isFirstLoad = !oldProduct;
            const productIdChanged = !!oldId && oldId !== newId;
            if (productIdChanged) {
                pricingChannelId.value = createChannelId();
                resetPricingState();
            }
            if (props.isOpen && (isFirstLoad || productIdChanged || !hasPricingConfig.value)) {
                initializeDefaultSize({ triggerPricingUpdate: false });
                calculateInitialPrice();
            }
        }, { immediate: true });
        watch(() => props.isOpen, (isOpen, wasOpen) => {
            if (isOpen && !wasOpen && props.product) {
                initializeDefaultSize({ triggerPricingUpdate: false });
                calculateInitialPrice();
            }
            if (!isOpen) {
                pricingChannelId.value = createChannelId();
                resetPricingState();
            }
        });
        // 🔥 SIMPLIFIED: Direct computed props - NO intermediate reactive layer
        const selectedSizeProp = computed(() => {
            return selectedSize.value;
        });
        // 🔥 CRITICAL: Force dependency on version by accessing it first!
        const serverPriceProp = computed(() => {
            void pricingStateVersion.value; // ← Force dependency tracking
            return price.value;
        });
        const serverPriceCentProp = computed(() => {
            void pricingStateVersion.value; // ← Force dependency tracking
            return priceCent.value;
        });
        const formattedServerPriceProp = computed(() => {
            void pricingStateVersion.value; // ← Force dependency tracking
            return formattedPrice.value;
        });
        const isPriceLoadingProp = computed(() => {
            return isLoading.value;
        });
        // Watch for reactivity updates
        watch([price, priceCent, formattedPrice, pricingStateVersion], () => {
            // Trigger computed props by accessing them
            void serverPriceProp.value;
            void serverPriceCentProp.value;
            void formattedServerPriceProp.value;
        }, { immediate: true });
        watch(selectedSize, () => {
            // Trigger re-evaluation when size changes
            void selectedSizeProp.value;
        }, { immediate: true, deep: true });
        const formattedButtonPrice = computed(() => {
            if (pricingError.value) {
                return 'Preisfehler';
            }
            if (isLoading.value) {
                return 'Berechnung...';
            }
            // Menü-Kontext: nicht den Einzelpreis, sondern „inklusive" bzw. den Mehrbetrag
            // (Größe/Extras über dem Basispreis) anzeigen – der Basispreis steckt im Menüpreis.
            if (props.priceMode === 'menu') {
                const p = props.product;
                const baseCent = p?.priceCent != null ? Number(p.priceCent) : (p?.price != null ? Number(p.price) * 100 : 0);
                const perUnitCent = quantity.value > 0 ? Math.round(priceCent.value / quantity.value) : priceCent.value;
                const surchargeCent = perUnitCent - baseCent;
                if (!Number.isFinite(surchargeCent) || surchargeCent <= 0)
                    return 'inklusive';
                return `+ ${headerCurrencyFormatter.format(surchargeCent / 100)}`;
            }
            return formattedPrice.value;
        });
        const canAddToCart = computed(() => {
            if (!props.product?.available) {
                console.log('❌ [ConfigModalBase] Product not available');
                return false;
            }
            if (pricingError.value) {
                console.log('❌ [ConfigModalBase] Pricing error:', pricingError.value);
                return false;
            }
            // ✅ Always access productConfigContentRef.value to establish reactivity tracking
            const childRef = productConfigContentRef.value;
            // Check if child component is mounted and has validation
            if (!childRef) {
                console.log('⏳ [ConfigModalBase] Child ref not yet available (component mounting)');
                const hasMandatoryIngredients = (props.product?.mandatoryIngredients?.length ?? 0) > 0;
                return !hasMandatoryIngredients;
            }
            // Check if isMandatoryIngredientsValid exists and is a ComputedRef
            if ('isMandatoryIngredientsValid' in childRef) {
                const validationValue = childRef.isMandatoryIngredientsValid;
                // ✅ defineExpose automatically unwraps refs, so this is the actual boolean value
                if (typeof validationValue === 'boolean') {
                    console.log('🔍 [ConfigModalBase] Mandatory ingredients validation:', {
                        isValid: validationValue,
                        productName: props.product?.name,
                        hasMandatoryIngredients: (props.product?.mandatoryIngredients?.length ?? 0) > 0
                    });
                    return validationValue;
                }
                else {
                    console.log('⚠️ [ConfigModalBase] Unexpected validation type:', typeof validationValue, validationValue);
                }
            }
            else {
                console.log('⚠️ [ConfigModalBase] Child ref has no isMandatoryIngredientsValid property');
            }
            // If no mandatory ingredients validation is needed, default to false when required
            const hasMandatoryIngredients = (props.product?.mandatoryIngredients?.length ?? 0) > 0;
            console.log('ℹ️ [ConfigModalBase] No validation available, has mandatory ingredients?', hasMandatoryIngredients);
            return !hasMandatoryIngredients;
        });
        // ✅ Defer pricing recalculation to next tick so Vue can finish its
        // current render cycle before the heavier reactive cascade kicks in.
        // This avoids "Cannot read properties of null (reading 'insertBefore'/'style')"
        // errors when checkbox toggles trigger downstream reactive updates.
        const schedulePricingUpdate = () => {
            void nextTick(() => {
                updatePricingConfiguration();
            });
        };
        // Quantity methods
        const increaseQuantity = () => {
            quantity.value++;
            schedulePricingUpdate();
        };
        const decreaseQuantity = () => {
            if (quantity.value > 1) {
                quantity.value--;
                schedulePricingUpdate();
            }
        };
        // Event handlers for ProductConfigContent
        const handleExtrasUpdate = (value) => {
            selectedExtras.value = value;
            schedulePricingUpdate();
        };
        const handleRemovedIngredientsUpdate = (value) => {
            removedIngredients.value = value;
            schedulePricingUpdate();
        };
        const handleSpecialInstructionsUpdate = (value) => {
            specialInstructions.value = value;
            // No price recalculation for instructions
        };
        const handleQuantityUpdate = (value) => {
            quantity.value = value;
            schedulePricingUpdate();
        };
        const handleSelectedSizeUpdate = (value) => {
            selectedSize.value = value;
            schedulePricingUpdate();
        };
        const getScrollbarWidth = () => {
            if (typeof document === 'undefined' || !document.body) {
                return 0;
            }
            const scrollDiv = document.createElement('div');
            scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;';
            document.body.appendChild(scrollDiv);
            const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
            document.body.removeChild(scrollDiv);
            return scrollbarWidth;
        };
        watch(() => props.isOpen, (isOpen) => {
            if (typeof document === 'undefined' || !document.body) {
                return;
            }
            if (isOpen) {
                document.body.style.overflow = 'hidden';
                document.body.style.paddingRight = getScrollbarWidth() + 'px';
                if (props.product?.hasSizes && props.product && !selectedSize.value) {
                    initializeDefaultSize({ triggerPricingUpdate: true });
                }
            }
            else {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            }
        });
        onUnmounted(() => {
            if (typeof document !== 'undefined' && document.body) {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            }
            eventBus.off('productPricing:result', handlePricingResultEvent);
            eventBus.off('productPricing:loading', handlePricingLoadingEvent);
        });
        function closeModal() {
            emit('close');
            resetForm();
        }
        function resetForm() {
            selectedExtras.value = [];
            removedIngredients.value = [];
            specialInstructions.value = '';
            quantity.value = 1;
            selectedSize.value = null;
            lastPricingPayload.value = null;
            pricingChannelId.value = createChannelId();
            resetPricingState();
            emit('pricing:reset');
        }
        const handleOverlayClick = (event) => {
            if (event.target === event.currentTarget) {
                closeModal();
            }
        };
        // Triggered when the footer button is tapped while mandatory fields are
        // still missing: scroll to + briefly highlight the first unfilled group.
        const handleHighlightInvalid = () => {
            productConfigContentRef.value?.highlightFirstInvalidGroup?.();
        };
        const addToCart = async () => {
            if (!props.product)
                return;
            // ✅ Use productConfigContentRef instead of props.validationRef
            const childRef = productConfigContentRef.value;
            if (childRef) {
                const isValid = childRef.validateMandatoryIngredients?.() ?? true;
                if (!isValid) {
                    console.warn('❌ [ConfigModalBase] Mandatory ingredients validation failed in addToCart');
                    return;
                }
            }
            // ✅ Get selections from buildPricingPayload conversion
            const payload = buildPricingPayload();
            const enrichedMandatorySelections = payload?.selectedMandatoryIngredients || [];
            console.log('🛒 [ConfigModalBase] Adding to cart with mandatory selections:', {
                count: enrichedMandatorySelections.length,
                selections: enrichedMandatorySelections
            });
            const calculatedPriceCent = priceCent.value;
            if (calculatedPriceCent > 0) {
                const configuredProduct = {
                    ...props.product,
                    id: props.product.id || props.product._id,
                    _id: props.product._id || props.product.id,
                    price: calculatedPriceCent / 100 / quantity.value,
                    totalPrice: calculatedPriceCent / 100,
                    quantity: quantity.value,
                    extras: selectedExtras.value,
                    removedIngredients: removedIngredients.value,
                    specialInstructions: specialInstructions.value,
                    selectedSize: selectedSize.value,
                    selectedSizeId: selectedSize.value?.id,
                    calculatedPriceCent: calculatedPriceCent,
                    selectedMandatoryIngredients: enrichedMandatorySelections
                };
                emit('add-to-cart', configuredProduct);
                closeModal();
            }
            else {
                console.error('❌ Server price not available yet! PriceCent:', priceCent.value, 'Price:', price.value);
            }
        };
        provide('modalState', {
            selectedExtras,
            removedIngredients,
            specialInstructions,
            quantity,
            selectedSize
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                _withDirectives(_createElementVNode("div", {
                    class: "fixed inset-0 z-50",
                    onClick: handleOverlayClick
                }, [
                    _createCommentVNode(" Backdrop "),
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300" }, null, -1 /* CACHED */)),
                    _createCommentVNode(" Responsive Modal - Single Structure "),
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", {
                            class: _normalizeClass(["h-full lg:h-auto w-full lg:max-w-2xl lg:max-h-[90vh] bg-white lg:rounded-2xl shadow-2xl transform transition-all duration-300 flex flex-col", _ctx.isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0']),
                            onClick: _cache[0] || (_cache[0] = _withModifiers(() => { }, ["stop"]))
                        }, [
                            _createCommentVNode(" Header (vereinheitlicht mit dem Menü-Modal: Bild links, Titel/Beschreibung, Preis + Schließen rechts) "),
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("div", _hoisted_3, [
                                    (_ctx.product?.image)
                                        ? (_openBlock(), _createElementBlock("img", {
                                            key: 0,
                                            src: _ctx.product.image,
                                            alt: _ctx.product?.name,
                                            class: "h-14 w-14 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                                        }, null, 8 /* PROPS */, _hoisted_4))
                                        : _createCommentVNode("v-if", true),
                                    _createElementVNode("div", _hoisted_5, [
                                        _createElementVNode("h2", _hoisted_6, _toDisplayString(_ctx.product?.name), 1 /* TEXT */),
                                        (_ctx.product?.description)
                                            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.product.description), 1 /* TEXT */))
                                            : _createCommentVNode("v-if", true)
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                    (headerPriceLabel.value)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(headerPriceLabel.value), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                    _createElementVNode("button", {
                                        type: "button",
                                        class: "p-2 -mr-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md",
                                        "aria-label": "Schließen",
                                        onClick: closeModal
                                    }, [...(_cache[1] || (_cache[1] = [
                                            _createElementVNode("i", { class: "fas fa-times text-lg" }, null, -1 /* CACHED */)
                                        ]))])
                                ])
                            ]),
                            _createCommentVNode(" Content "),
                            _withDirectives(_createElementVNode("div", _hoisted_10, [
                                _createCommentVNode(" @vue-ignore: Vue 3 auto-unwraps refs in template, TypeScript doesn't recognize this "),
                                _createCommentVNode(" ✅ Key only changes when product ID changes, NOT on size/price updates "),
                                (_openBlock(), _createBlock(ConfigContent, {
                                    key: productComponentKey.value,
                                    product: _ctx.product,
                                    selectedExtras: selectedExtras.value,
                                    removedIngredients: removedIngredients.value,
                                    specialInstructions: specialInstructions.value,
                                    quantity: quantity.value,
                                    selectedSize: selectedSizeProp.value,
                                    ingredientCategories: _ctx.ingredientCategories,
                                    allIngredients: _ctx.allIngredients,
                                    serverCalculatedPrice: serverPriceProp.value,
                                    serverCalculatedPriceCent: serverPriceCentProp.value,
                                    formattedServerPrice: formattedServerPriceProp.value,
                                    isPriceLoading: isPriceLoadingProp.value,
                                    "onUpdate:selectedExtras": handleExtrasUpdate,
                                    "onUpdate:removedIngredients": handleRemovedIngredientsUpdate,
                                    "onUpdate:specialInstructions": handleSpecialInstructionsUpdate,
                                    "onUpdate:quantity": handleQuantityUpdate,
                                    "onUpdate:selectedSize": handleSelectedSizeUpdate,
                                    ref_key: "productConfigContentRef",
                                    ref: productConfigContentRef
                                }, null, 8 /* PROPS */, ["product", "selectedExtras", "removedIngredients", "specialInstructions", "quantity", "selectedSize", "ingredientCategories", "allIngredients", "serverCalculatedPrice", "serverCalculatedPriceCent", "formattedServerPrice", "isPriceLoading"]))
                            ], 512 /* NEED_PATCH */), [
                                [_vShow, _ctx.product && _ctx.isOpen]
                            ]),
                            _createCommentVNode(" Footer with Dynamic Theme Colors "),
                            _createVNode(ConfigFooter, {
                                product: _ctx.product,
                                quantity: quantity.value,
                                "price-mode": _ctx.priceMode,
                                "can-add-to-cart": canAddToCart.value,
                                "formatted-button-price": formattedButtonPrice.value,
                                "pricing-error": pricingError.value,
                                "is-price-loading": isLoading.value,
                                onAddToCart: addToCart,
                                onIncreaseQuantity: increaseQuantity,
                                onDecreaseQuantity: decreaseQuantity,
                                onHighlightInvalid: handleHighlightInvalid
                            }, null, 8 /* PROPS */, ["product", "quantity", "price-mode", "can-add-to-cart", "formatted-button-price", "pricing-error", "is-price-loading"])
                        ], 2 /* CLASS */)
                    ])
                ], 512 /* NEED_PATCH */), [
                    [_vShow, _ctx.isOpen]
                ])
            ]));
        };
    }
});
